/*
  ps-promo-code.js — shared driver for the promo-code announcement bar and the
  PDP promo-code badge (Figma 1944:3971).

  Both surfaces are rendered hidden and stay hidden unless a promo code is
  actually in play. A code is resolved once per session, in this order:

    1. a ?discount= (or ?promo= / ?discount_code=) parameter on the URL — the
       link Shopify itself uses, so the code is already applied at checkout;
    2. a code resolved earlier this session and kept in sessionStorage;
    3. the fallback code typed into the theme editor, if one is set.

  The countdown is anchored to the moment the code was first resolved, so it
  keeps counting down across page views instead of restarting on every load,
  and it freezes at 00:00 rather than hiding the offer the visitor came for.
*/
(function () {
  'use strict';

  var CODE_KEY = 'psPromoCode';
  var START_KEY = 'psPromoStartedAt';
  var APPLIED_PREFIX = 'psPromoApplied:';
  var URL_PARAMS = ['discount', 'discount_code', 'promo'];

  /* sessionStorage throws outright in some privacy modes, so every access is guarded. */
  function readStore(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* Nothing to do — the promo still works, it just restarts next page. */
    }
  }

  /* Shopify discount codes are case-insensitive but display uppercase. */
  function normalise(raw) {
    if (!raw) return '';
    return String(raw).trim().replace(/\s+/g, '').slice(0, 64);
  }

  function codeFromUrl() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return '';
    }
    for (var i = 0; i < URL_PARAMS.length; i++) {
      var value = normalise(params.get(URL_PARAMS[i]));
      if (value) return value;
    }
    return '';
  }

  /*
    Asks Shopify to put the code on the session so it lands at checkout. Only
    needed for the theme-editor fallback: a ?discount= link has already been
    honoured by Shopify before this script runs. /cart.js is the cheapest
    internal page to be redirected to once the cookie is set.
  */
  function applyAtCheckout(code) {
    if (readStore(APPLIED_PREFIX + code)) return;
    writeStore(APPLIED_PREFIX + code, '1');
    try {
      window.fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
        credentials: 'same-origin'
      })['catch'](function () {});
    } catch (e) {
      /* An invalid or expired code just means no discount — never a blocker. */
    }
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function resolveCode(root) {
    var fromUrl = codeFromUrl();
    if (fromUrl) return { code: fromUrl, fromUrl: true };

    var stored = normalise(readStore(CODE_KEY));
    if (stored) return { code: stored, fromUrl: false };

    var fallback = normalise(root.getAttribute('data-ps-promo-fallback'));
    if (fallback) return { code: fallback, fromUrl: false };

    return { code: '', fromUrl: false };
  }

  /*
    The start time is shared by both surfaces, so the bar and the badge always
    show the same remaining time. A start time already in the future, or older
    than the countdown itself, is treated as this page load.
  */
  function resolveStart(duration) {
    var now = Date.now();
    var stored = parseInt(readStore(START_KEY), 10);
    if (!isNaN(stored) && stored <= now && now - stored < duration * 1000) {
      return stored;
    }
    writeStore(START_KEY, String(now));
    return now;
  }

  function startCountdown(root, duration, startedAt) {
    var minutesEl = root.querySelector('[data-ps-promo-minutes]');
    var secondsEl = root.querySelector('[data-ps-promo-seconds]');
    if (!minutesEl || !secondsEl) return;

    var timer = null;

    function tick() {
      var elapsed = Math.floor((Date.now() - startedAt) / 1000);
      var remaining = Math.max(0, duration - elapsed);

      minutesEl.textContent = pad(Math.floor(remaining / 60));
      secondsEl.textContent = pad(remaining % 60);

      /* Freeze at 00:00 — the offer stays visible, the clock just stops. */
      if (remaining === 0 && timer) {
        window.clearInterval(timer);
        timer = null;
        root.setAttribute('data-ps-promo-expired', 'true');
      }
    }

    tick();
    timer = window.setInterval(tick, 1000);
  }

  function activate(root) {
    if (root.getAttribute('data-ps-promo-ready') === 'true') return;

    var resolved = resolveCode(root);
    if (!resolved.code) return;

    writeStore(CODE_KEY, resolved.code);

    if (!resolved.fromUrl && root.getAttribute('data-ps-promo-apply') === 'true') {
      applyAtCheckout(resolved.code);
    }

    var labels = root.querySelectorAll('[data-ps-promo-code]');
    for (var i = 0; i < labels.length; i++) {
      labels[i].textContent = resolved.code;
    }

    var duration = parseInt(root.getAttribute('data-ps-promo-duration'), 10);
    if (isNaN(duration) || duration < 0) duration = 600;

    startCountdown(root, duration, resolveStart(duration));

    root.setAttribute('data-ps-promo-ready', 'true');
    root.hidden = false;
  }

  function init() {
    var roots = document.querySelectorAll('[data-ps-promo]');
    for (var i = 0; i < roots.length; i++) {
      activate(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', init);
})();
